import { apiFetch, ApiError } from '@/lib/http';
import { SURFACE_CAP } from '@/lib/sync/create-surface';
import type { TemplateDiagramKind, TemplateSourceKind, TemplateSummary } from './catalog';

export type SourceScope = { kind: 'full' } | { kind: 'excerpt'; excerptId: string };

export type TemplateSourceRequest =
  | { kind: 'document'; entityId: string; scope?: SourceScope }
  | { kind: 'transcript'; recordingId: string }
  | { kind: 'surface'; entityId: string; surfaceId: string; scope: SourceScope }
  | { kind: 'calendar_event'; eventId: string; entityId: string };

export interface StartRunResult {
  runId: string;
  status: string;
}

export interface TemplateRunSnapshot {
  runId: string;
  stage: string;
  surfaceId: string | null;
  error: { code: string; retryable: boolean } | null;
}

interface TemplateRunView {
  runId: string;
  stage: string;
  surfaceId?: string;
  error?: { code: string; message: string; retryable: boolean };
}

export function startTemplateRun(
  templateId: string,
  body: {
    sources: TemplateSourceRequest[];
    target: { kind: 'new-surface'; entityId: string };
  },
): Promise<StartRunResult> {
  return apiFetch<StartRunResult>(`/templates/${templateId}/runs`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export interface RegenerateTarget {
  entityId: string;
  surfaceId: string;
}

export function regenerateSurface(
  templateId: string,
  target: RegenerateTarget,
  opts?: { useLatestVersion?: boolean },
): Promise<StartRunResult> {
  const body = {
    target: { kind: 'existing-surface', ...target },
    ...(opts?.useLatestVersion ? { useLatestVersion: true } : {}),
  };
  return apiFetch<StartRunResult>(`/templates/${encodeURIComponent(templateId)}/runs`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export interface ProposalSection {
  key: string;
  heading: string;
  current: string;
  proposed: string;
  changed: boolean;
  outcome?: SectionOutcome | null;
}

export interface Proposal {
  proposalId: string;
  templateId: string;
  outputType: 'text' | 'canvas';
  sections: ProposalSection[];
  fromExcerpt: boolean;
}

function proposalPath(entityId: string, surfaceId: string): string {
  return `/entities/${encodeURIComponent(entityId)}/surfaces/${encodeURIComponent(surfaceId)}/proposal`;
}

export async function getProposal(entityId: string, surfaceId: string): Promise<Proposal | null> {
  try {
    return await apiFetch<Proposal>(proposalPath(entityId, surfaceId));
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}

export interface SectionDecision {
  key: string;
  accept: boolean;
  force?: boolean;
}

export type ApplyRequest =
  | { proposalId: string; decisions: SectionDecision[] }
  | { proposalId: string; accept: boolean; force?: boolean };

export type SectionOutcome = 'applied' | 'kept' | 'conflicted';

export interface ApplyResult {
  state: 'open' | 'closed';
  sections: { key: string; outcome: SectionOutcome }[];
}

export function applyProposal(entityId: string, surfaceId: string, body: ApplyRequest): Promise<ApplyResult> {
  return apiFetch<ApplyResult>(`${proposalPath(entityId, surfaceId)}/apply`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function dismissProposal(entityId: string, surfaceId: string, proposalId?: string): Promise<void> {
  return apiFetch<void>(`${proposalPath(entityId, surfaceId)}/dismiss`, {
    method: 'POST',
    body: JSON.stringify(proposalId ? { proposalId } : {}),
  });
}

/** `TemplateListItem` as `GET /templates` sends it. */
interface TemplateListItem {
  id: string;
  name: string;
  description: string;
  owner: TemplateSummary['owner'];
  outputType: TemplateSummary['outputType'];
  diagramKind: TemplateDiagramKind | null;
  sections: { key: string; heading: string; required: boolean }[];
  sourceKinds: TemplateSourceKind[];
  lastUsedAt: string | null;
  currentVersion: number;
}

/** Every template the caller can start, in the server's order. */
export async function listTemplates(): Promise<TemplateSummary[]> {
  const items = await apiFetch<TemplateListItem[]>('/templates');
  return items
    .filter((item) => Number.isInteger(item.currentVersion) && item.currentVersion >= 1)
    .map((item) => ({
      id: item.id,
      name: item.name,
      description: item.description,
      owner: item.owner,
      outputType: item.outputType,
      diagramKind: item.diagramKind,
      sections: item.sections.map(({ key, heading, required }) => ({ key, heading, required })),
      sourceKinds: item.sourceKinds,
      lastUsedAt: item.lastUsedAt,
      currentVersion: item.currentVersion,
    }));
}

export interface CapturedExcerpt {
  excerptId: string;
  markdown: string;
  preview: string;
}

export function captureExcerpt(body: {
  entityId: string;
  surfaceId: string | null;
  markdown: string;
}): Promise<CapturedExcerpt> {
  return apiFetch<CapturedExcerpt>('/template-excerpts', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function getTemplateRun(
  templateId: string,
  runId: string,
): Promise<TemplateRunSnapshot> {
  const view = await apiFetch<TemplateRunView>(`/templates/${templateId}/runs/${runId}`);
  return {
    runId: view.runId,
    stage: view.stage,
    surfaceId: view.surfaceId ?? null,
    error: view.error ? { code: view.error.code, retryable: view.error.retryable } : null,
  };
}

const REFUSAL_STATUSES = [402, 403, 404, 409];

export function isServerError(error: unknown): boolean {
  return error instanceof ApiError && error.status >= 500;
}

export function isContractViolation(error: unknown): boolean {
  return error instanceof ApiError && error.status === 400;
}

export function isStartRefusal(error: unknown): boolean {
  return error instanceof ApiError && REFUSAL_STATUSES.includes(error.status);
}

export function startRefusalMessage(error: unknown, sourceTitle: string): string {
  const status = error instanceof ApiError ? error.status : undefined;
  switch (status) {
    case 402:
      return 'This workspace is read-only, so nothing new can be added.';
    case 403:
      return 'You need edit access to this document to add to it.';
    case 404:
      return error instanceof ApiError && error.code === 'template_not_found'
        ? "This template isn't available any more."
        : `${sourceTitle} can't be found any more.`;
    case 409:
      return error instanceof ApiError && error.code === 'surface_cap_reached'
        ? `This document already has ${SURFACE_CAP} of ${SURFACE_CAP} added.`
        : 'Something went wrong on our side.';
    default:
      return 'Something went wrong on our side.';
  }
}

export const PASSAGE_CHANGED = 'The text changed while you were choosing. Select it again.';

export function isCaptureRefusal(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  if (error.status === 409 || error.status === 403 || error.status === 404) return true;
  return (
    error.status === 400 && (error.code === 'passage_too_large' || error.code === 'passage_empty')
  );
}

export function captureRefusalMessage(error: unknown, sourceTitle: string): string {
  if (error instanceof ApiError) {
    if (error.status === 409 || error.code === 'passage_not_found') return PASSAGE_CHANGED;
    if (error.status === 400 && error.code === 'passage_too_large') {
      return 'That passage is too long. Select less of it.';
    }
    if (error.status === 400 && error.code === 'passage_empty') {
      return 'That selection has no text to read.';
    }
    if (error.status === 403) return `You can't open ${sourceTitle} any more.`;
    if (error.status === 404) return `${sourceTitle} can't be found any more.`;
  }
  return 'Something went wrong on our side.';
}
